import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import { inputClass } from './whatsappCampaignFormUtils';

type Props = {
  templates: WhatsAppTemplate[];
  value: string;
  onChange: (templateName: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
};

const WhatsAppTemplateSearchSelect: React.FC<Props> = ({
  templates,
  value,
  onChange,
  placeholder = 'Search template…',
  emptyLabel = 'Same as campaign',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = templates.find((t) => t.name === value);
  const selectedLabel = selected ? selected.displayName || selected.name : value ? value : emptyLabel;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const hay = `${t.displayName || ''} ${t.name} ${t.languageCode || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [templates, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
        }}
        className={`${inputClass} flex items-center justify-between gap-1 text-left text-xs`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900">
          <div className="flex items-center gap-1 border-b px-2 py-1.5 dark:border-gray-700">
            <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent py-1 text-xs outline-none"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                {emptyLabel}
              </button>
            </li>
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">No templates match</li>
            ) : (
              filtered.map((t) => (
                <li key={t.name}>
                  <button
                    type="button"
                    className={`w-full px-3 py-1.5 text-left text-xs hover:bg-indigo-50 dark:hover:bg-indigo-950/40 ${
                      t.name === value ? 'bg-indigo-50 font-medium dark:bg-indigo-950/30' : ''
                    }`}
                    onClick={() => {
                      onChange(t.name);
                      setOpen(false);
                    }}
                  >
                    <span className="block truncate">{t.displayName || t.name}</span>
                    {t.displayName && t.displayName !== t.name ? (
                      <span className="block truncate text-[10px] text-gray-400">{t.name}</span>
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplateSearchSelect;
